/**
 * Outil « Lignes de niveau et gradient » — logique pure (MPSI/PTSI → PT).
 *
 * f(x, y) polynomiale (coefficients rationnels) : dérivées partielles
 * SYMBOLIQUES et valeurs EXACTES en un point A au quart d'unité ; le gradient
 * ∇f(A) = (∂f/∂x(A), ∂f/∂y(A)) est orthogonal à la ligne de niveau
 * {f = f(A)} et dirigé vers les valeurs croissantes. Lignes de niveau par
 * « marching squares » (numérique). Points critiques des fonctions proposées
 * donnés avec leur nature et la raison (notations des cartes « Dérivées
 * partielles et gradient », « Points critiques et extrema »).
 */

import { add, frac, isZero, mul, value, type Frac } from '../../ui/exact';

/** monôme c · x^i · y^j */
export interface Mono {
  c: Frac;
  i: number;
  j: number;
}
export type Poly2 = Mono[];

const m = (c: number, i: number, j: number, q = 1): Mono => ({ c: frac(c, q), i, j });

export function dx(p: Poly2): Poly2 {
  return p.filter((t) => t.i > 0).map((t) => ({ c: mul(t.c, frac(t.i)), i: t.i - 1, j: t.j }));
}
export function dy(p: Poly2): Poly2 {
  return p.filter((t) => t.j > 0).map((t) => ({ c: mul(t.c, frac(t.j)), i: t.i, j: t.j - 1 }));
}

const powF = (x: Frac, k: number): Frac => {
  let r = frac(1);
  for (let n = 0; n < k; n++) r = mul(r, x);
  return r;
};
/** valeur exacte en (x, y) rationnels */
export function evalExact(p: Poly2, x: Frac, y: Frac): Frac {
  return p.reduce((s, t) => add(s, mul(t.c, mul(powF(x, t.i), powF(y, t.j)))), frac(0));
}
export const evalNum = (p: Poly2, x: number, y: number) =>
  p.reduce((s, t) => s + value(t.c) * x ** t.i * y ** t.j, 0);

/** décimal (pas 0,25) → fraction */
export const fromDec = (v: number) => frac(Math.round(v * 10000), 10000);

/* ---------- écriture ---------- */

const SUP: Record<number, string> = { 2: '²', 3: '³', 4: '⁴', 5: '⁵' };
const varPow = (v: string, k: number) => (k === 0 ? '' : k === 1 ? v : `${v}${SUP[k] ?? `^${k}`}`);

export function fracText(f: Frac): string {
  const s = f.p < 0 ? '−' : '';
  const n = Math.abs(f.p);
  return f.q === 1 ? `${s}${n}` : `${s}${n}/${f.q}`;
}

/** décimal exact (q = 2ⁱ5ʲ) sinon fraction : « 1,25 », « −2/3 » */
export function numText(f: Frac): string {
  let q = f.q;
  while (q % 2 === 0) q /= 2;
  while (q % 5 === 0) q /= 5;
  if (q === 1)
    return String(Number(value(f).toFixed(6)))
      .replace('.', ',')
      .replace('-', '−');
  return fracText(f);
}

/** « x²y + y³ », « 2xy », « −3 », « 0 » (ordre des monômes de la définition) */
export function polyText(p: Poly2): string {
  const terms = p.filter((t) => !isZero(t.c));
  if (!terms.length) return '0';
  return terms
    .map((t, k) => {
      const neg = t.c.p < 0;
      const abs = frac(Math.abs(t.c.p), t.c.q);
      const vars = `${varPow('x', t.i)}${varPow('y', t.j)}`;
      const coef = vars && abs.p === 1 && abs.q === 1 ? '' : fracText(abs);
      const body = `${coef}${vars}`;
      return k === 0 ? `${neg ? '−' : ''}${body}` : `${neg ? '− ' : '+ '}${body}`;
    })
    .join(' ');
}

/* ---------- fonctions proposées ---------- */

export type Nature = 'min' | 'max' | 'col' | 'sans';
export interface Critical {
  x: number;
  y: number;
  nature: Nature;
  /** minimum ou maximum global ? */
  global?: boolean;
  why: string;
  /** extremum seulement local : un point où f fait mieux */
  notGlobal?: string;
}
export interface Preset {
  id: string;
  f: Poly2;
  /** résolution de ∇f = 0 */
  solve: string;
  criticals: Critical[];
}

export const PRESETS: Preset[] = [
  {
    id: 'bowl',
    f: [m(1, 2, 0), m(1, 0, 2)],
    solve: '∇f = (2x ; 2y) = (0 ; 0) ⟺ (x ; y) = (0 ; 0).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'min',
        global: true,
        why: 'f(x, y) = x² + y² ≥ 0 = f(0, 0) sur tout ℝ².',
      },
    ],
  },
  {
    id: 'saddle',
    f: [m(1, 2, 0), m(-1, 0, 2)],
    solve: '∇f = (2x ; −2y) = (0 ; 0) ⟺ (x ; y) = (0 ; 0).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'col',
        why: 'f(x, 0) = x² > 0 pour x ≠ 0 et f(0, y) = −y² < 0 pour y ≠ 0 : aussi près de (0 ; 0) qu’on veut, f prend des valeurs plus grandes et plus petites que f(0, 0) = 0 — ni minimum ni maximum.',
      },
    ],
  },
  {
    id: 'shifted',
    f: [m(1, 2, 0), m(1, 0, 2), m(-2, 1, 0)],
    solve: '∇f = (2x − 2 ; 2y) = (0 ; 0) ⟺ (x ; y) = (1 ; 0).',
    criticals: [
      {
        x: 1,
        y: 0,
        nature: 'min',
        global: true,
        why: 'f(x, y) = (x − 1)² + y² − 1 ≥ −1 = f(1, 0) sur tout ℝ².',
      },
    ],
  },
  {
    id: 'card',
    f: [m(1, 2, 1), m(1, 0, 3)],
    solve: '∇f = (2xy ; x² + 3y²) = (0 ; 0) : x² + 3y² = 0 impose (x ; y) = (0 ; 0).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'sans',
        why: 'f(0, 0) = 0 et f(0, y) = y³ est > 0 pour y > 0, < 0 pour y < 0 : pas d’extremum en (0 ; 0).',
      },
    ],
  },
  {
    id: 'cross',
    f: [m(1, 2, 0), m(1, 1, 1), m(1, 0, 2)],
    solve: '∇f = (2x + y ; x + 2y) = (0 ; 0) ⟺ (x ; y) = (0 ; 0).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'min',
        global: true,
        why: 'f(x, y) = (x + y/2)² + 3y²/4 ≥ 0 = f(0, 0) sur tout ℝ².',
      },
    ],
  },
  {
    id: 'xy',
    f: [m(1, 1, 1)],
    solve: '∇f = (y ; x) = (0 ; 0) ⟺ (x ; y) = (0 ; 0).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'col',
        why: 'Pour t ≠ 0 : f(t, t) = t² > 0 et f(t, −t) = −t² < 0, alors que f(0, 0) = 0 : ni minimum ni maximum.',
      },
    ],
  },
  {
    id: 'cubic',
    f: [m(1, 3, 0), m(-3, 1, 0), m(1, 0, 2)],
    solve: '∇f = (3x² − 3 ; 2y) = (0 ; 0) ⟺ x = ±1 et y = 0.',
    criticals: [
      {
        x: 1,
        y: 0,
        nature: 'min',
        global: false,
        why: 'f(x, y) + 2 = (x − 1)²(x + 2) + y² ≥ 0 dès que x > −2 : minimum local −2 en (1 ; 0). Pas global : f(−3, 0) = −18 < −2.',
        notGlobal: 'f(−3, 0) = −18 < −2 = f(1, 0) : ailleurs, f prend des valeurs plus petites.',
      },
      {
        x: -1,
        y: 0,
        nature: 'col',
        why: 'f(−1, 0) = 2 ; f(x, 0) − 2 = (x + 1)²(x − 2) < 0 pour x ≠ −1 proche de −1, et f(−1, y) − 2 = y² > 0 pour y ≠ 0 : ni minimum ni maximum.',
      },
    ],
  },
  {
    id: 'folium',
    f: [m(1, 3, 0), m(1, 0, 3), m(-3, 1, 1)],
    solve:
      '∇f = (3x² − 3y ; 3y² − 3x) = (0 ; 0) ⟺ y = x² et x = y², d’où x⁴ = x : (x ; y) = (0 ; 0) ou (1 ; 1).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'col',
        why: 'Pour t ≠ 0 proche de 0 : f(t, −t) = 3t² > 0 et f(t, t) = t²(2t − 3) < 0, alors que f(0, 0) = 0 : ni minimum ni maximum.',
      },
      {
        x: 1,
        y: 1,
        nature: 'min',
        global: false,
        why: 'Avec x = 1 + h, y = 1 + k : f(x, y) + 1 = 3h² − 3hk + 3k² + h³ + k³, et la partie quadratique 3(h − k/2)² + 9k²/4 l’emporte près de (0 ; 0) : minimum local −1 en (1 ; 1). Pas global : f(−2, 0) = −8 < −1.',
        notGlobal: 'f(−2, 0) = −8 < −1 = f(1, 1) : ailleurs, f prend des valeurs plus petites.',
      },
    ],
  },
  {
    id: 'cap',
    f: [m(4, 0, 0), m(-1, 2, 0), m(-2, 0, 2)],
    solve: '∇f = (−2x ; −4y) = (0 ; 0) ⟺ (x ; y) = (0 ; 0).',
    criticals: [
      {
        x: 0,
        y: 0,
        nature: 'max',
        global: true,
        why: 'f(x, y) = 4 − x² − 2y² ≤ 4 = f(0, 0) sur tout ℝ².',
      },
    ],
  },
];

export const presetById = (id: string) => PRESETS.find((p) => p.id === id) ?? PRESETS[0]!;

export const NATURE_TEXT: Record<Nature, string> = {
  min: 'minimum',
  max: 'maximum',
  col: 'point col',
  sans: 'pas d’extremum',
};

/* ---------- valeurs en un point ---------- */

export interface AtPoint {
  f: Frac;
  fx: Frac;
  fy: Frac;
  critical: boolean;
}

export function at(p: Poly2, x: number, y: number): AtPoint {
  const X = fromDec(x);
  const Y = fromDec(y);
  const fx = evalExact(dx(p), X, Y);
  const fy = evalExact(dy(p), X, Y);
  return { f: evalExact(p, X, Y), fx, fy, critical: isZero(fx) && isZero(fy) };
}

/* ---------- lignes de niveau (marching squares) ---------- */

export interface Seg {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** segments de la ligne {f = c} sur [a ; b] × [a2 ; b2] (carré par défaut), grille n × n */
export function contour(
  f: (x: number, y: number) => number,
  c: number,
  a: number,
  b: number,
  n = 60,
  a2 = a,
  b2 = b,
): Seg[] {
  const h = (b - a) / n;
  const k = (b2 - a2) / n;
  const v: number[][] = [];
  for (let i = 0; i <= n; i++) {
    v.push([]);
    for (let j = 0; j <= n; j++) v[i]!.push(f(a + i * h, a2 + j * k) - c);
  }
  const segs: Seg[] = [];
  const lerp = (x0: number, y0: number, f0: number, x1: number, y1: number, f1: number) => {
    const t = f0 === f1 ? 0.5 : f0 / (f0 - f1);
    return [x0 + (x1 - x0) * t, y0 + (y1 - y0) * t] as const;
  };
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++) {
      const x0 = a + i * h;
      const y0 = a2 + j * k;
      const x1 = x0 + h;
      const y1 = y0 + k;
      // coins : bas-gauche, bas-droite, haut-droite, haut-gauche
      const f00 = v[i]![j]!;
      const f10 = v[i + 1]![j]!;
      const f11 = v[i + 1]![j + 1]!;
      const f01 = v[i]![j + 1]!;
      const pts: (readonly [number, number])[] = [];
      if (f00 > 0 !== f10 > 0) pts.push(lerp(x0, y0, f00, x1, y0, f10));
      if (f10 > 0 !== f11 > 0) pts.push(lerp(x1, y0, f10, x1, y1, f11));
      if (f11 > 0 !== f01 > 0) pts.push(lerp(x1, y1, f11, x0, y1, f01));
      if (f01 > 0 !== f00 > 0) pts.push(lerp(x0, y1, f01, x0, y0, f00));
      if (pts.length === 2)
        segs.push({ x1: pts[0]![0], y1: pts[0]![1], x2: pts[1]![0], y2: pts[1]![1] });
      else if (pts.length === 4) {
        // cas ambigu (col) : points pris dans l'ordre bas, droite, haut, gauche.
        // Centre du signe de f00 : la région de f00 traverse la case, on isole
        // les coins 10 (bas + droite) et 01 (haut + gauche) ; sinon 00 et 11.
        const center = f(x0 + h / 2, y0 + k / 2) - c > 0;
        const pair = center === f00 > 0 ? [0, 1, 2, 3] : [0, 3, 1, 2];
        segs.push({
          x1: pts[pair[0]!]![0],
          y1: pts[pair[0]!]![1],
          x2: pts[pair[1]!]![0],
          y2: pts[pair[1]!]![1],
        });
        segs.push({
          x1: pts[pair[2]!]![0],
          y1: pts[pair[2]!]![1],
          x2: pts[pair[3]!]![0],
          y2: pts[pair[3]!]![1],
        });
      }
    }
  // segments réduits à un point (niveau passant par un sommet de la grille)
  return segs.filter((g) => Math.hypot(g.x2 - g.x1, g.y2 - g.y1) > 1e-12);
}

/**
 * niveaux « ronds » ({1 ; 2 ; 2,5 ; 5} × 10ᵏ, le plus grand pas ≤ (max − min)/want)
 * entre min et max de f sur [a ; b]², plus les valeurs `extra` (valeurs critiques)
 */
export function levels(
  f: (x: number, y: number) => number,
  a: number,
  b: number,
  want = 10,
  extra: readonly number[] = [],
) {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i <= 40; i++)
    for (let j = 0; j <= 40; j++) {
      const v = f(a + ((b - a) * i) / 40, a + ((b - a) * j) / 40);
      lo = Math.min(lo, v);
      hi = Math.max(hi, v);
    }
  const raw = (hi - lo) / want;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [5, 2.5, 2, 1].map((k) => k * p).find((s) => s <= raw) ?? p;
  const out: number[] = [];
  for (let c = Math.ceil(lo / step) * step; c <= hi; c += step) out.push(Number(c.toFixed(10)));
  for (const e of extra)
    if (e > lo && e < hi && out.every((c) => Math.abs(c - e) > step / 4)) out.push(e);
  out.sort((u, v) => u - v);
  return { levels: out, step, lo, hi };
}

/* ---------- hessienne, tangente ---------- */

/** dérivées secondes r = ∂²f/∂x², s = ∂²f/∂x∂y, t = ∂²f/∂y² en (x, y) entiers ou décimaux */
export function hessian(p: Poly2, x: number, y: number) {
  const X = fromDec(x);
  const Y = fromDec(y);
  const r = evalExact(dx(dx(p)), X, Y);
  const s2 = evalExact(dx(dy(p)), X, Y);
  const t = evalExact(dy(dy(p)), X, Y);
  const det = add(mul(r, t), mul(frac(-1), mul(s2, s2)));
  const tr = add(r, t);
  const verdict: Nature | 'doute' =
    det.p > 0 ? (r.p > 0 ? 'min' : 'max') : det.p < 0 ? 'col' : 'doute';
  return { r, s: s2, t, det, tr, verdict };
}

const gcdI = (u: number, v: number): number => {
  u = Math.abs(u);
  v = Math.abs(v);
  while (v) [u, v] = [v, u % v];
  return u;
};
const lcmI = (u: number, v: number) => (u / gcdI(u, v)) * v;

/** coefficients entiers premiers entre eux, proportionnels (même sens) à ceux donnés */
export function integerRatio(fs: readonly Frac[]): number[] {
  const L = fs.reduce((acc, f) => lcmI(acc, f.q), 1);
  const ints = fs.map((f) => (f.p * L) / f.q);
  const g = ints.reduce((acc, v) => gcdI(acc, v), 0) || 1;
  return ints.map((v) => v / g);
}

/** « x + y = 2 », « 2x − y = 3 », « y = −1 » : équation ax + by = c réduite */
export function lineEq(a: Frac, b: Frac, c: Frac): string {
  let [A, B, C] = integerRatio([a, b, c]);
  if (A! < 0 || (A === 0 && B! < 0)) [A, B, C] = [-A!, -B!, -C!];
  const term = (k: number, v: string, first: boolean) => {
    if (k === 0) return '';
    const abs = Math.abs(k) === 1 ? v : `${Math.abs(k)}${v}`;
    return first ? `${k < 0 ? '−' : ''}${abs}` : ` ${k < 0 ? '−' : '+'} ${abs}`;
  };
  const left = `${term(A!, 'x', true)}${term(B!, 'y', A === 0)}`;
  return `${left} = ${String(C).replace('-', '−')}`;
}

/** « (−1 ; 1) » : vecteur à coordonnées entières premières entre elles, même direction et même sens */
export function intVec(u: Frac, v: Frac): string {
  const [U, V] = integerRatio([u, v]);
  return `(${String(U).replace('-', '−')} ; ${String(V).replace('-', '−')})`;
}

/* ---------- défi ---------- */

export type QuizKind = 'partial' | 'grad' | 'orth' | 'critical' | 'nature';
export const QUIZ_KINDS: QuizKind[] = ['partial', 'grad', 'orth', 'critical', 'nature'];

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}
export interface Quiz {
  kind: QuizKind;
  prompt: string;
  options: QuizOption[];
  explain: string;
}

type Rng = () => number;
const pick = <T>(xs: readonly T[], rng: Rng): T => xs[Math.floor(rng() * xs.length)]!;
function shuffle<T>(a: readonly T[], rng: Rng): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}
/**
 * La bonne réponse d'abord dans `cands`, puis les distracteurs : les `sure`
 * premiers sont toujours gardés, les autres sont mélangés AVANT d'en garder assez.
 */
function finish(
  kind: QuizKind,
  prompt: string,
  cands: QuizOption[],
  explain: string,
  rng: Rng,
  sure = 0,
): Quiz | null {
  const seen = new Set<string>();
  const kept = cands.filter((c) => (seen.has(c.text) ? false : (seen.add(c.text), true)));
  if (kept.length < 4) return null;
  const [good, ...rest] = kept;
  const ordered = [...rest.slice(0, sure), ...shuffle(rest.slice(sure), rng)];
  return { kind, prompt, options: shuffle([good!, ...ordered].slice(0, 4), rng), explain };
}

/** regroupe les monômes semblables, retire les nuls */
export function simplify(p: Poly2): Poly2 {
  const out: Poly2 = [];
  for (const t of p) {
    const same = out.find((u) => u.i === t.i && u.j === t.j);
    if (same) same.c = add(same.c, t.c);
    else out.push({ ...t });
  }
  return out.filter((t) => !isZero(t.c));
}

/** dériver « en traitant aussi l'autre variable » : on dérive chaque monôme par rapport à x ET y */
function bothDerivative(p: Poly2): Poly2 {
  return simplify([...dx(p), ...dy(p)]);
}

const ptText = (x: number, y: number) => `(${x} ; ${y})`.replace(/-(?=\d)/g, '−');

function quizPartial(rng: Rng): Quiz | null {
  const pr = pick(
    PRESETS.filter((p) => p.f.length >= 2),
    rng,
  );
  const wrt = rng() < 0.5 ? 'x' : 'y';
  const good = wrt === 'x' ? dx(pr.f) : dy(pr.f);
  const other = wrt === 'x' ? dy(pr.f) : dx(pr.f);
  return finish(
    'partial',
    `f(x, y) = ${polyText(pr.f)}. Que vaut ∂f/∂${wrt} ?`,
    [
      { text: polyText(good), correct: true },
      {
        text: polyText(other),
        correct: false,
        hint: `C’est ∂f/∂${wrt === 'x' ? 'y' : 'x'} : on a dérivé par rapport à l’autre variable.`,
      },
      {
        text: polyText(bothDerivative(pr.f)),
        correct: false,
        hint: `En dérivant par rapport à ${wrt}, l’autre variable est une CONSTANTE : on ne la dérive pas.`,
      },
      {
        text: polyText(good.map((t) => ({ ...t, c: mul(t.c, frac(-1)) }))),
        correct: false,
        hint: 'Erreur de signe sur chaque terme.',
      },
      {
        text: polyText(pr.f.filter((t) => (wrt === 'x' ? t.i > 0 : t.j > 0))),
        correct: false,
        hint: `Il faut dériver les termes qui contiennent ${wrt}, pas seulement les garder.`,
      },
    ],
    `On dérive par rapport à ${wrt} en traitant ${wrt === 'x' ? 'y' : 'x'} comme une constante : ∂f/∂${wrt} = ${polyText(good)}.`,
    rng,
  );
}

function quizGrad(rng: Rng): Quiz | null {
  const pr = pick(PRESETS, rng);
  const x = pick([-2, -1, 1, 2], rng);
  const y = pick([-2, -1, 1, 2], rng);
  const a = at(pr.f, x, y);
  if (a.critical || (a.fx.p === a.fy.p && a.fx.q === a.fy.q)) return null;
  const v = (u: Frac, w: Frac) => `(${numText(u)} ; ${numText(w)})`;
  const neg = (u: Frac) => mul(u, frac(-1));
  return finish(
    'grad',
    `f(x, y) = ${polyText(pr.f)}. Que vaut ∇f(${x}, ${y}) ?`.replace(/-(?=\d)/g, '−'),
    [
      { text: v(a.fx, a.fy), correct: true },
      {
        text: v(a.fy, a.fx),
        correct: false,
        hint: 'Composantes échangées : ∇f = (∂f/∂x ; ∂f/∂y), dans cet ordre.',
      },
      {
        text: v(neg(a.fx), neg(a.fy)),
        correct: false,
        hint: 'Le gradient pointe vers les valeurs CROISSANTES de f : vérifie les signes.',
      },
      {
        text: v(a.f, a.f),
        correct: false,
        hint: 'C’est f(A) répété : le gradient est formé des dérivées partielles.',
      },
      { text: v(a.fx, neg(a.fy)), correct: false, hint: 'Signe de ∂f/∂y à revoir.' },
    ],
    `∂f/∂x = ${polyText(dx(pr.f))} et ∂f/∂y = ${polyText(dy(pr.f))} ; en ${ptText(x, y)} : ∇f = ${v(a.fx, a.fy)}.`,
    rng,
  );
}

function quizOrth(rng: Rng): Quiz | null {
  const variant = pick(['orth', 'dir', 'zero'] as const, rng);
  if (variant === 'orth')
    return finish(
      'orth',
      'En un point A où ∇f(A) ≠ 0, quelle est la position du gradient par rapport à la ligne de niveau de f passant par A ?',
      [
        { text: 'Orthogonal à la ligne de niveau', correct: true },
        {
          text: 'Tangent à la ligne de niveau',
          correct: false,
          hint: 'Le long de la ligne de niveau, f ne varie pas : sa dérivée dans cette direction, ⟨∇f(A), u⟩, est nulle.',
        },
        {
          text: 'Sans lien avec la ligne de niveau',
          correct: false,
          hint: 'Le développement limité f(A + h) = f(A) + ⟨∇f(A), h⟩ + o(‖h‖) lie les deux.',
        },
        {
          text: 'Orthogonal seulement si f est un polynôme',
          correct: false,
          hint: 'Le résultat vaut pour toute f de classe C¹ dès que ∇f(A) ≠ 0.',
        },
      ],
      'Le long de la ligne de niveau, f reste égale à f(A) : la dérivée dans la direction tangente, ⟨∇f(A), u⟩, est nulle, donc ∇f(A) est orthogonal à la ligne de niveau.',
      rng,
    );
  if (variant === 'dir')
    return finish(
      'orth',
      'Pour quel vecteur unitaire u la dérivée directionnelle Dᵤf(A) = ⟨∇f(A), u⟩ est-elle la plus grande (∇f(A) ≠ 0) ?',
      [
        { text: 'u = ∇f(A)/‖∇f(A)‖', correct: true },
        {
          text: 'u = −∇f(A)/‖∇f(A)‖',
          correct: false,
          hint: 'Là, Dᵤf(A) = −‖∇f(A)‖ : c’est la plus PETITE, f décroît le plus vite.',
        },
        {
          text: 'u tangent à la ligne de niveau',
          correct: false,
          hint: 'u est alors orthogonal à ∇f(A) : Dᵤf(A) = 0, f ne varie pas au premier ordre.',
        },
        {
          text: 'u = (1 ; 0)',
          correct: false,
          hint: 'Alors Dᵤf(A) = ∂f/∂x(A) : ce n’est le maximum que si ∇f(A) a la direction et le sens de (1 ; 0).',
        },
      ],
      'Cauchy-Schwarz : ⟨∇f(A), u⟩ ≤ ‖∇f(A)‖ ‖u‖ = ‖∇f(A)‖, avec égalité si et seulement si u = ∇f(A)/‖∇f(A)‖ (même direction ET même sens) : f croît le plus vite dans le sens de ∇f(A).',
      rng,
    );
  return finish(
    'orth',
    'Que peut-on dire d’un point A où ∇f(A) = 0 (f de classe C¹ sur un ouvert) ?',
    [
      { text: 'C’est un point critique : peut-être un extremum, peut-être pas', correct: true },
      {
        text: 'f y a forcément un extremum local',
        correct: false,
        hint: 'La réciproque est fausse : x² − y² a un gradient nul en (0 ; 0), c’est un point col.',
      },
      {
        text: 'f y a forcément un minimum',
        correct: false,
        hint: '4 − x² − 2y² a un gradient nul en (0 ; 0), et c’est un maximum.',
      },
      {
        text: '∇f(A) est un vecteur normal à la ligne de niveau en A',
        correct: false,
        hint: 'Un vecteur normal est non nul ; avec ∇f(A) = 0, la ligne de niveau peut se réduire à un point (x² + y²) ou former une croix (x² − y²).',
      },
    ],
    'Un extremum local sur un ouvert est un point critique, mais un point critique n’est pas toujours un extremum (point col).',
    rng,
  );
}

function quizCritical(rng: Rng): Quiz | null {
  // une fois sur deux, une fonction dont un point critique n'est pas l'origine
  const off = PRESETS.filter((p) => p.criticals.some((c) => c.x !== 0 || c.y !== 0));
  const pr = rng() < 0.5 ? pick(off, rng) : pick(PRESETS, rng);
  const c = pick(pr.criticals, rng);
  // pièges : une seule dérivée partielle s'annule
  const half: [number, number][] = [];
  const others: [number, number][] = [];
  for (let x = -2; x <= 2; x++)
    for (let y = -2; y <= 2; y++) {
      const a = at(pr.f, x, y);
      if (a.critical) continue;
      if (isZero(a.fx) || isZero(a.fy)) half.push([x, y]);
      else if (Math.abs(x - c.x) + Math.abs(y - c.y) <= 2) others.push([x, y]);
    }
  const traps = [...shuffle(half, rng).slice(0, 2), ...shuffle(others, rng)];
  return finish(
    'critical',
    `f(x, y) = ${polyText(pr.f)}. Lequel de ces points est un point critique de f ?`,
    [
      { text: ptText(c.x, c.y), correct: true },
      ...traps.map(([x, y]) => {
        const a = at(pr.f, x, y);
        const why = isZero(a.fx)
          ? `∂f/∂x s’y annule, mais ∂f/∂y = ${numText(a.fy)} ≠ 0`
          : isZero(a.fy)
            ? `∂f/∂y s’y annule, mais ∂f/∂x = ${numText(a.fx)} ≠ 0`
            : `∇f = (${numText(a.fx)} ; ${numText(a.fy)}) ≠ 0`;
        return { text: ptText(x, y), correct: false, hint: `En ${ptText(x, y)}, ${why}.` };
      }),
    ],
    pr.solve,
    rng,
  );
}

const OPT = {
  minG: 'Un minimum global',
  minL: 'Un minimum local, non global',
  max: 'Un maximum',
  min: 'Un minimum',
  col: 'Un point col (ni minimum ni maximum)',
  sans: 'Ni minimum ni maximum',
  rien: 'On ne peut rien dire : ∇f = 0 ne suffit pas',
  ext: 'Un extremum, puisque ∇f = 0',
};
const HINT = {
  notMinAbove: 'Aussi près de A qu’on veut, il y a des points M avec f(M) < f(A) : pas un minimum.',
  notMaxBelow: 'Aussi près de A qu’on veut, il y a des points M avec f(M) > f(A) : pas un maximum.',
  isMinNotMax: 'f(A) est la plus PETITE valeur près de A, pas la plus grande.',
  isMaxNotMin: 'f(A) est la plus GRANDE valeur près de A, pas la plus petite.',
  notCol: 'f − f(A) garde un signe constant près de A : c’est un extremum, pas un col.',
  conclude:
    '∇f = 0 ne suffit pas en général, mais ici l’étude du signe de f − f(A) permet de conclure.',
  necessary: '∇f = 0 est une condition NÉCESSAIRE d’extremum, pas suffisante.',
  global: 'L’inégalité vaut sur tout ℝ² : l’extremum est global.',
};

function quizNature(rng: Rng): Quiz | null {
  const pr = pick(PRESETS, rng);
  const c = pick(pr.criticals, rng);
  let opts: QuizOption[];
  if (c.nature === 'min')
    opts = c.global
      ? [
          { text: OPT.minG, correct: true },
          { text: OPT.minL, correct: false, hint: HINT.global },
          { text: OPT.max, correct: false, hint: HINT.isMinNotMax },
          { text: OPT.col, correct: false, hint: HINT.notCol },
          { text: OPT.rien, correct: false, hint: HINT.conclude },
        ]
      : [
          { text: OPT.minL, correct: true },
          { text: OPT.minG, correct: false, hint: c.notGlobal ?? '' },
          { text: OPT.max, correct: false, hint: HINT.isMinNotMax },
          { text: OPT.col, correct: false, hint: HINT.notCol },
          { text: OPT.rien, correct: false, hint: HINT.conclude },
        ];
  else if (c.nature === 'max')
    opts = [
      { text: 'Un maximum global', correct: true },
      { text: 'Un maximum local, non global', correct: false, hint: HINT.global },
      { text: OPT.min, correct: false, hint: HINT.isMaxNotMin },
      { text: OPT.col, correct: false, hint: HINT.notCol },
      { text: OPT.rien, correct: false, hint: HINT.conclude },
    ];
  else
    opts = [
      { text: c.nature === 'col' ? OPT.col : OPT.sans, correct: true },
      { text: OPT.min, correct: false, hint: HINT.notMinAbove },
      { text: OPT.max, correct: false, hint: HINT.notMaxBelow },
      { text: OPT.ext, correct: false, hint: HINT.necessary },
    ];
  return finish(
    'nature',
    `f(x, y) = ${polyText(pr.f)} a un point critique en ${ptText(c.x, c.y)}. Quelle est la nature de ce point critique ?`,
    opts,
    c.why,
    rng,
    // « global » / « local, non global » : toujours proposés ensemble
    c.nature === 'min' || c.nature === 'max' ? 1 : 0,
  );
}

const MAKERS: Record<QuizKind, (rng: Rng) => Quiz | null> = {
  partial: quizPartial,
  grad: quizGrad,
  orth: quizOrth,
  critical: quizCritical,
  nature: quizNature,
};

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  for (let i = 0; i < 500; i++) {
    const q = MAKERS[kind](rng);
    if (q) return q;
  }
  throw new Error(`défi ${kind} introuvable`);
}
